// Marketplace order-state vocabulary. Values are the raw upstream states stored
// in `marketplace_orders.state`. The name stays vendor-neutral per the
// multi-marketplace rule; values keep Mirakl's uppercase and MPAPI's lowercase
// wire forms unchanged.

const MIRAKL_ORDER_STATES = [
  "STAGING",
  "WAITING_ACCEPTANCE",
  "WAITING_DEBIT",
  "WAITING_DEBIT_PAYMENT",
  "SHIPPING",
  "SHIPPED",
  "TO_COLLECT",
  "RECEIVED",
  "CLOSED",
  "INCIDENT_OPEN",
  "REFUSED",
  "CANCELED",
] as const

export const MPAPI_ORDER_STATES = [
  "blocked",
  "open",
  "shipping",
  "shipped",
  "delivered",
  "returned",
  "cancelled",
  "lost",
] as const

export const MARKETPLACE_ORDER_STATES = [...MIRAKL_ORDER_STATES, ...MPAPI_ORDER_STATES] as const

export type MarketplaceOrderState = (typeof MARKETPLACE_ORDER_STATES)[number]

/** Semantic tone for a marketplace order state, for UI colouring. Mirrors
 *  `omsStateTone`. Conservative on purpose: only terminal outcomes carry a
 *  colour (RECEIVED/delivered = success; refused, canceled, returned, and lost
 *  = failure; INCIDENT_OPEN = attention). Every in-flight state — and CLOSED,
 *  which Mirakl applies at end-of-lifecycle regardless of outcome — reads as
 *  neutral `progress`.
 *  Cross-system contradictions are surfaced by the Problems banner, not by
 *  colouring an in-flight state. */
export type MarketplaceStateTone = "success" | "progress" | "attention" | "failure"

export function marketplaceStateTone(
  state: string | null | undefined
): MarketplaceStateTone | null {
  if (!state) {
    return null
  }
  switch (state.toUpperCase()) {
    case "RECEIVED":
    case "DELIVERED":
      return "success"
    case "REFUSED":
    case "CANCELED":
    case "CANCELLED":
    case "RETURNED":
    case "LOST":
      return "failure"
    case "INCIDENT_OPEN":
      return "attention"
    default:
      return "progress"
  }
}

/** Human-readable label for a marketplace order state, matching what the
 *  marketplace's own seller console displays. The stored `state` is the raw
 *  vendor state (e.g. Mirakl `SHIPPING`), which doesn't read the way an
 *  operator expects — Mirakl/Decathlon shows `SHIPPING` as "Awaiting shipment"
 *  (accepted by the seller, not yet handed to the carrier). We surface the
 *  console label so Nova and the marketplace agree word-for-word. Unknown
 *  states fall back to the raw value. */
const MARKETPLACE_STATE_LABELS: Record<MarketplaceOrderState, string> = {
  STAGING: "Staging",
  WAITING_ACCEPTANCE: "To accept",
  WAITING_DEBIT: "Awaiting debit",
  WAITING_DEBIT_PAYMENT: "Awaiting debit payment",
  SHIPPING: "Awaiting shipment",
  SHIPPED: "Shipped",
  TO_COLLECT: "To collect",
  RECEIVED: "Received",
  CLOSED: "Closed",
  INCIDENT_OPEN: "Incident open",
  REFUSED: "Refused",
  CANCELED: "Canceled",
  blocked: "Blocked",
  open: "Open",
  shipping: "Shipping",
  shipped: "Shipped",
  delivered: "Delivered",
  returned: "Returned",
  cancelled: "Cancelled",
  lost: "Lost",
}

export function marketplaceStateLabel(state: string | null | undefined): string {
  if (!state) {
    return "—"
  }
  return (
    MARKETPLACE_STATE_LABELS[state as MarketplaceOrderState] ??
    MARKETPLACE_STATE_LABELS[state.toUpperCase() as MarketplaceOrderState] ??
    state
  )
}
